import { ReactNode } from "react";

import { Icon } from "./IconControls/IconControls";

import styles from "./ExpandableSection.module.scss";

type ExpandableSectionVariant = "brand";
type ExpandableSectionCategory = "primary" | "secondary";

/**
 * The props for the {@link ExpandableSection} component.
 */
interface IExpandableSectionProps {
    /**
     * The title of the expandable section.
     */
    title: string | JSX.Element;

    /**
     * Whether the section is open (true) or collapsed (false).
     */
    isOpen: boolean;

    /**
     * Whether the section should be locked in an open state, regardless of {@link isOpen}. Default false.
     */
    lockOpen?: boolean;

    /**
     * Whether to show the chevron icon to toggle {@link isOpen}. Default false.
     */
    showChevronIcon?: boolean;

    /**
     * The label text for the expand/collapse icon.
     */
    chevronLabel?: JSX.Element;

    /**
     * A callback to handle the expand/collapse icon click.
     */
    onChevronClick: () => void;

    /**
     * The components to render within the expandable section.
     */
    children: ReactNode;

    /**
     * Id usefull when creating unit tests
     */
    dataCy?: string;

    /**
     * The variant of the expandable section, which can control various styles such as background color and title.
     */
    variant?: ExpandableSectionVariant;

    /**
     * The category of the expandable section, which control the background color.
     */
    category?: ExpandableSectionCategory;
}

const variantBackgroundMapper: Record<ExpandableSectionVariant, string> = {
    "brand": styles.backgroundBrand
};

const categoryBackgroundMapper: Record<ExpandableSectionCategory, string> = {
    "primary": styles.backgroundPrimary,
    "secondary": styles.backgroundSecondary
};

/**
 * A section which can be expanded or collapsed to toggling its content's visibility.
 * @param {IExpandableSectionProps} props The options to apply.
 */
function ExpandableSection(props: Readonly<IExpandableSectionProps>): JSX.Element {
    const showChevron = props.showChevronIcon ?? true;
    const chevronContainerStyle = `${styles.chevronContain}${showChevron ? " " + styles.chevronIsVisible : ""}`;
    const variantStyle = props.variant ? variantBackgroundMapper[props.variant] : "";
    const categoryStyle = props.variant ? categoryBackgroundMapper[props.category] : "";

    return (
        <div className={`${variantStyle} ${categoryStyle}`}>
            <div data-cy={props.dataCy} className={styles.sectionHeader}>
                {typeof props.title === "string" ? (<h4>{props.title}</h4>) : (<>{props.title}</>)}
                <div data-cy={`${props.dataCy}-chevron`} className={chevronContainerStyle} {...(showChevron ? { onClick: () => props.onChevronClick() } : {})}>
                    {props.chevronLabel}
                    {showChevron && (
                        <div data-cy={`${props.dataCy}-chevron-icon`}>
                            <Icon type={props.isOpen ? "chevron-up" : "chevron-down"} />
                        </div>
                    )}
                </div>
            </div>
            {(props.isOpen || props.lockOpen) && (
                <div data-cy={`${props.dataCy}-content`} className={styles.sectionContent}>
                    {props.children}
                </div>
            )}
        </div>
    );
}

export { ExpandableSection };
